import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";

import { AxiosError } from "axios";
import { IS_MCDAN_ENV } from "constants/app.constants";

import useColumnsDictionary from "hooks/useColumnsDictionary";

import { EntityKind } from "models/entityKind";

import {
  CustomField,
  useFetchCustomFields
} from "components/sync/hooks/useFetchCustomFields";

import {
  UploadExcelFileResponse,
  useAddField,
  useDeleteField,
  useExcelDropzone,
  useExcelTemplateDownload,
  useExcelUpload as useExcelValidationUpload,
  useReplaceScenarioFields,
  useUpdateField,
  useUploadExcelFile
} from ".";
import { StickDataQueryKey } from "../../pad-generator/hooks/useStickData";
import { ScenarioField } from "../types/field";
import { StickFieldsTemplate } from "../types/template";
import { defaultNotificationService } from "../utils/notifications";
import { useStickFieldTemplates } from "./useStickFieldTemplates";

export interface UseStickDataManagementControllerProps {
  scenarioId?: string;
  schema?: string;
  wells: Array<{ uwid: string }>;
  fields?: ScenarioField[];
  onFieldsChange?: (fields: ScenarioField[]) => void;
}

export interface UseStickDataManagementControllerReturn {
  // Fields state
  selectedFields: ScenarioField[];
  expectedHeaders: string[];

  // Custom fields
  customFields: CustomField[];
  isLoadingCustomFields: boolean;
  customFieldsError: Error | null;

  // Field management handlers
  handleAddField: (field: ScenarioField) => void;
  handleRemoveField: (fieldId: string) => void;
  handleCustomFieldAssign: (fieldId: string, customFieldId: string | null) => void;

  // Template management
  templates: StickFieldsTemplate[];
  isTemplatesLoading: boolean;
  hasTemplateChanges: boolean;
  selectedTemplateId: string | undefined;
  isReplacingFields: boolean;
  handleSelectTemplate: (id: string | undefined) => void;
  handleSaveTemplate: (name: string, projectId: string) => Promise<void>;
  handleClearAllFields: () => void;
  handleUpdateTemplate: (id: string) => Promise<void>;
  handleRenameTemplate: (id: string, newName: string) => Promise<void>;
  handleDeleteTemplate: (id: string) => Promise<void>;

  // Template download
  onDownloadTemplate: () => void;
  isDownloading: boolean;

  // File upload
  handleFileUploadWithApi: (file: File) => Promise<void>;
  uploadedData: UploadExcelFileResponse | null;
  uploadError: string | null;
  isUploading: boolean;
  validationError: string | null;
  // Drag and drop
  isDragging: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
}

export const useStickDataManagementController = ({
  scenarioId,
  schema,
  wells,
  fields = [],
  onFieldsChange
}: UseStickDataManagementControllerProps): UseStickDataManagementControllerReturn => {
  const queryClient = useQueryClient();
  // Assumes Well entity kind; UDC fields from other entity types won't be found
  // in this dict and will fall back to the template's stored name.
  const columnDict = useColumnsDictionary(EntityKind.Well);
  // The working field set is initialised from the scenario's persisted fields
  // (fetched in PadScenario.tsx, passed via the `fields` prop). Templates are a
  // convenience layer that can replace this set, but never gate it.
  const [selectedFields, setSelectedFields] = useState<ScenarioField[]>(fields);

  // Keep the working set in sync with persisted fields. On scenario change,
  // also clear the template selection — templates don't carry across scenarios.
  const prevScenarioIdRef = useRef(scenarioId);
  useEffect(() => {
    const scenarioChanged = prevScenarioIdRef.current !== scenarioId;
    prevScenarioIdRef.current = scenarioId;
    setSelectedFields(fields);
    if (scenarioChanged) setSelectedTemplateId(undefined);
  }, [scenarioId, fields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve each field's display name from the live column dictionary so the
  // unit suffix tracks the user's current default-unit setting (e.g. switching
  // metres → feet turns "Hz Length (m)" into "Hz Length (ft)"). The stored
  // `name` is only a snapshot from selection time; `columnDict[field].title`
  // is rebuilt reactively when units change. Fields absent from the dictionary
  // (UDC/custom/legacy) keep their stored name.
  const displayFields = useMemo(
    () =>
      selectedFields.map((field) => ({
        ...field,
        name: columnDict[field.field]?.title ?? field.name
      })),
    [selectedFields, columnDict]
  );

  // Compute expected headers from the live display names. The downloaded
  // template uses the display name as the column header, so client-side upload
  // validation must match against the display name too.
  const expectedHeaders = useMemo(
    () => ["Stick Name", ...displayFields.map((field) => field.name)],
    [displayFields]
  );

  // Hook: Fetch custom fields for the current schema (only in mcdan environment)
  const {
    data: customFieldsData,
    isLoading: isLoadingCustomFields,
    error: customFieldsError
  } = useFetchCustomFields(IS_MCDAN_ENV ? schema : undefined);

  const customFields = useMemo(
    () => customFieldsData?.customFields ?? [],
    [customFieldsData]
  );

  // Hook: Field Management
  const addFieldMutation = useAddField();
  const deleteFieldMutation = useDeleteField();
  const replaceFieldsMutation = useReplaceScenarioFields();
  const updateFieldMutation = useUpdateField();

  // Hook: API Upload
  const uploadMutation = useUploadExcelFile();
  const [uploadedData, setUploadedData] = useState<UploadExcelFileResponse | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Hook: Excel Upload (client-side validation)
  const { isUploading, validationError, clearUploadedData } = useExcelValidationUpload({
    expectedHeaders,
    onError: (error) => {
      setUploadError(error);
    }
  });

  const resetUploadedData = () => {
    clearUploadedData();
    setUploadedData(null);
  };

  // Hook: Excel Template Download
  const { isDownloading, handleDownloadTemplate } = useExcelTemplateDownload({
    scenarioId,
    wells,
    onError: (error) => {
      defaultNotificationService.error(error);
    }
  });

  // Handler: File upload with API integration
  const handleFileUploadWithApi = async (file: File) => {
    if (scenarioId) {
      // API upload path: skip client-side header validation — the backend
      // handles header remapping (display names ↔ property paths) and
      // validates the file itself. Running client-side validation here would
      // reject old templates whose headers are property paths, not display names.
      uploadMutation.mutate(
        {
          file,
          scenarioId
        },
        {
          onError: (err: AxiosError<{ error: string; details: string }>) => {
            setUploadError(err?.response?.data?.error ?? "Error uploading Excel file");
          },
          onSuccess: (data) => {
            setUploadError(null);
            setUploadedData(data);
            // Invalidate stick data queries to refetch updated data
            queryClient.invalidateQueries([StickDataQueryKey]);
          }
        }
      );
    }
  };

  // Hook: Drag and Drop
  const { isDragging, handleDragOver, handleDragLeave, handleDrop } = useExcelDropzone({
    onFileDrop: handleFileUploadWithApi,
    onError: (error) => {
      setUploadError(error);
    },
    disabled: isUploading || uploadMutation.isLoading
  });

  // Handler: Add field
  const handleAddField = (field: ScenarioField) => {
    if (selectedFields.some((f) => f.field === field.field)) {
      return;
    }

    const previousFields = selectedFields;
    const newFields = [...selectedFields, field];
    setSelectedFields(newFields);
    if (onFieldsChange) onFieldsChange(newFields);
    if (uploadedData) resetUploadedData();

    if (scenarioId) {
      addFieldMutation.mutate(
        { field, scenarioId },
        {
          onError: (error: AxiosError<{ error?: string }>) => {
            setSelectedFields(previousFields);
            if (onFieldsChange) onFieldsChange(previousFields);
            setUploadError(error.response?.data?.error || "Failed to add field");
          }
        }
      );
    }
  };

  // Handler: Remove field
  const handleRemoveField = (field: string) => {
    if (!field) return;

    const fieldToRemove = selectedFields.find((f) => f.field === field);
    if (!fieldToRemove) return;

    const previousFields = selectedFields;
    const newFields = selectedFields.filter((f) => f.field !== field);
    setSelectedFields(newFields);
    if (onFieldsChange) onFieldsChange(newFields);
    if (uploadedData) resetUploadedData();

    if (scenarioId) {
      deleteFieldMutation.mutate(
        { field: fieldToRemove, scenarioId },
        {
          onError: (error: AxiosError<{ error?: string }>) => {
            setSelectedFields(previousFields);
            if (onFieldsChange) onFieldsChange(previousFields);
            setUploadError(error.response?.data?.error || "Failed to remove field");
          }
        }
      );
    }
  };

  // Handler: Download template (wrapper to pass selected fields)
  const onDownloadTemplate = () => {
    handleDownloadTemplate(selectedFields);
  };

  // Handler: Assign custom field to a field
  const handleCustomFieldAssign = (fieldId: string, customFieldId: string | null) => {
    if (!IS_MCDAN_ENV) {
      return; // Custom fields only available in mcdan environment
    }

    if (!scenarioId) {
      return; // Cannot update without a scenario
    }

    // Find the full CustomField object from customFields list
    const customField = customFieldId
      ? customFields.find((cf) => cf.id === customFieldId) || null
      : null;

    updateFieldMutation.mutate(
      { field: fieldId, scenarioId, customField },
      {
        onSuccess: (response) => {
          // Update selectedFields with sync_field information from response
          // Using functional state update to avoid stale closure issues
          setSelectedFields((currentFields) => {
            const updatedFields: ScenarioField[] = currentFields.map((field) => {
              const backendField = response.fields.find((f) => f.field === field.field);
              if (backendField) {
                return {
                  ...field,
                  syncField: backendField.sync_field
                };
              }
              return field;
            });
            return updatedFields;
          });

          // Force refetch of stick data with updated syncField
          // Invalidate queries first, then explicitly refetch to ensure fresh data
          queryClient.invalidateQueries([StickDataQueryKey]);
        },
        onError: (error: AxiosError<{ error?: string }>) => {
          const errorMessage = error.response?.data?.error || "Failed to update field";
          setUploadError(errorMessage);

          // Show error notification to user
          const fieldName =
            selectedFields.find((f) => f.field === fieldId)?.name || fieldId;
          defaultNotificationService.error(
            `Failed to update field "${fieldName}": ${errorMessage}`
          );

          // Capture error in Sentry
          Sentry.captureException(error);
        }
      }
    );
  };

  useEffect(() => {
    setUploadError(null);
    resetUploadedData();
    queryClient.invalidateQueries([StickDataQueryKey]);
  }, [scenarioId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Template management — compare selectedFields directly against the stored template
  // so hasChanges is false immediately after selecting or saving a template.
  const {
    templates,
    isLoading: isTemplatesLoading,
    saveTemplate,
    updateTemplate,
    renameTemplate,
    deleteTemplate
  } = useStickFieldTemplates();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(
    undefined
  );

  const hasTemplateChanges = useMemo(() => {
    if (!selectedTemplateId) return selectedFields.length > 0;
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (!template) return selectedFields.length > 0;
    if (selectedFields.length !== template.fields.length) return true;
    const templateFieldMap = new Map(
      template.fields.map((f) => [f.field, f.syncField ?? null])
    );
    return selectedFields.some(
      (f) =>
        !templateFieldMap.has(f.field) ||
        templateFieldMap.get(f.field) !== (f.syncField ?? null)
    );
  }, [selectedFields, selectedTemplateId, templates]);

  const handleSaveTemplate = async (name: string, projectId: string): Promise<void> => {
    try {
      const newTemplate = await saveTemplate(name, selectedFields, projectId);
      setSelectedTemplateId(newTemplate.id);
    } catch {
      defaultNotificationService.error("Failed to save template");
    }
  };

  // Clear every selected field via a single atomic PUT to replace all fields with
  // an empty list. Does not change the selected template.
  const handleClearAllFields = () => {
    if (selectedFields.length === 0) return;

    const previousFields = selectedFields;
    setSelectedFields([]);
    if (onFieldsChange) onFieldsChange([]);
    if (uploadedData) resetUploadedData();

    if (scenarioId) {
      replaceFieldsMutation.mutate(
        { scenarioId, fields: [] },
        {
          onError: (error: AxiosError<{ error?: string }>) => {
            setSelectedFields(previousFields);
            if (onFieldsChange) onFieldsChange(previousFields);
            setUploadError(error.response?.data?.error || "Failed to clear fields");
          }
        }
      );
    }
  };

  const handleUpdateTemplate = async (id: string): Promise<void> => {
    try {
      await updateTemplate(id, selectedFields);
    } catch {
      defaultNotificationService.error("Failed to save template");
    }
  };

  const handleRenameTemplate = async (id: string, newName: string): Promise<void> => {
    try {
      await renameTemplate(id, newName);
    } catch {
      defaultNotificationService.error("Failed to rename template");
    }
  };

  const handleDeleteTemplate = async (id: string): Promise<void> => {
    const nextTemplate = templates
      .filter((t) => t.id !== id)
      .sort((a, b) => a.name.localeCompare(b.name))[0];

    try {
      await deleteTemplate(id);
      if (selectedTemplateId === id) {
        // Select the next template, or clear the selection (and fields) if none remain.
        handleSelectTemplate(nextTemplate ? nextTemplate.id : undefined);
      }
    } catch {
      defaultNotificationService.error("Failed to delete template");
    }
  };

  // Loading a template completely replaces the working field set with the
  // template's fields via a single atomic PUT. This avoids partial-state races
  // from per-field add/delete mutations triggering mid-diff refetches.
  // Clearing the selection (id undefined) just empties the working set without
  // backend writes.
  const handleSelectTemplate = (id: string | undefined) => {
    const previousFields = selectedFields;
    const previousTemplateId = selectedTemplateId;
    setSelectedTemplateId(id);
    const template = id ? templates.find((t) => t.id === id) : undefined;
    // Enrich template fields with bracket names from the scenario's stored fields.
    // Templates saved before display names were persisted store plain names (e.g.
    // "Zone" instead of "Zone (gDC Prod Formation)"). Resolution order:
    // 1. scenario's persisted fields (most specific — already bracket-qualified)
    // 2. column service dictionary (covers fields not yet in this scenario)
    // 3. template's stored name (last resort for completely unknown fields)
    const scenarioNameMap = new Map(fields.map((f) => [f.field, f.name]));
    const nextFields = template
      ? template.fields.map((f) => ({
          ...f,
          name: scenarioNameMap.get(f.field) ?? columnDict[f.field]?.title ?? f.name
        }))
      : [];
    setSelectedFields(nextFields);
    if (onFieldsChange) onFieldsChange(nextFields);
    if (uploadedData) resetUploadedData();

    if (scenarioId && id) {
      replaceFieldsMutation.mutate(
        { scenarioId, fields: nextFields },
        {
          onError: (error: AxiosError<{ error?: string }>) => {
            setSelectedFields(previousFields);
            setSelectedTemplateId(previousTemplateId);
            if (onFieldsChange) onFieldsChange(previousFields);
            setUploadError(error.response?.data?.error || "Failed to load template");
          }
        }
      );
    }
  };

  return {
    // Fields state — expose the live, unit-resolved display names. Mutation
    // handlers still operate on the canonical `selectedFields` state internally.
    selectedFields: displayFields,
    expectedHeaders,

    // Custom fields
    customFields,
    isLoadingCustomFields,
    customFieldsError,

    // Field management handlers
    handleAddField,
    handleRemoveField,
    handleCustomFieldAssign,

    // Template download
    onDownloadTemplate,
    isDownloading,

    // File upload
    handleFileUploadWithApi,
    uploadedData,
    isUploading: isUploading || uploadMutation.isLoading,
    validationError,
    uploadError,
    // Drag and drop
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,

    // Template management
    templates,
    isTemplatesLoading,
    hasTemplateChanges,
    selectedTemplateId,
    isReplacingFields: replaceFieldsMutation.isLoading,
    handleSelectTemplate,
    handleSaveTemplate,
    handleClearAllFields,
    handleUpdateTemplate,
    handleRenameTemplate,
    handleDeleteTemplate
  };
};
